import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  IconButton,
  Tooltip,
  Alert,
  Button,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  WorkOutline as HiredIcon,
  DateRange as DateIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  CircleNotifications as CircleNotificationsIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  buscarEstatisticasEmpresa,
  EmpresaStats,
  buscarEmpresaPorId,
} from "../../services/empresa";
import { Empresa } from "../../types/empresa/empresa";
import { getApiBaseUrl } from "../../services/baseUrl";
import {
  buscarCandidaturasPendentes,
  buscarCandidatosProcessados,
} from "../../services/vaga";
import { useTheme } from "../../contexts/ThemeContext";
import { normalizarNome, normalizarEmail } from "../../utils/normalization";
import { obterIdEmpresa } from "../../utils/empresa";

interface StatCard {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
  hasNew?: boolean;
  newCount?: number;
}

const DashboardEmpresa: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [stats, setStats] = useState<EmpresaStats>({
    minhasVagas: 0,
    vagasAtivas: 0,
    candidaturasPendentes: 0,
    candidatosAprovados: 0,
    vagasFinalizadas: 0,
  });

  const [loadingCharts, setLoadingCharts] = useState(true);
  const [candidatosContratados, setCandidatosContratados] = useState(0);
  const [rankingCandidatos, setRankingCandidatos] = useState<
    Array<{
      nome: string;
      email: string;
      totalCandidaturas: number;
      ultimaCandidatura: string;
    }>
  >([]);

  // Estados para ranking de vagas mais procuradas
  const [rankingVagas, setRankingVagas] = useState<
    Array<{
      id: number;
      titulo: string;
      totalCandidaturas: number;
      dataPublicacao: string;
    }>
  >([]);
  const [expandedVagas, setExpandedVagas] = useState(false);
  const [expandedRanking, setExpandedRanking] = useState(false);

  useEffect(() => {
    const executarCarregamentos = async () => {
      try {
        // Fazer carregamentos em paralelo quando possível
        await Promise.allSettled([
          carregarDadosEmpresa(),
          carregarEstatisticas(),
        ]);

        // Carregamentos que dependem dos dados acima ou são independentes
        Promise.allSettled([
          carregarCandidatosContratados(),
          carregarRankingCandidatos(),
          carregarRankingVagas(),
        ]);
      } catch (error) {
        console.error("Erro no carregamento inicial:", error);
      }
    };

    // Só executar se estivermos na página do dashboard
    // Sem sessão de empresa (ex.: navegador logado como prefeitura) não há o que
    // carregar: a tela mostra o aviso abaixo em vez de estatísticas zeradas
    if (location.pathname === "/dashboard-empresa" && obterIdEmpresa()) {
      executarCarregamentos();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {}, [empresa]); // Dependência mais específica

  const carregarDadosEmpresa = async () => {
    try {
      // Primeiro tenta pegar do localStorage (salvo no login)
      const empresaNome =
        localStorage.getItem("empresaNome") ||
        sessionStorage.getItem("empresaNome");
      const empresaId =
        localStorage.getItem("empresaId") ||
        sessionStorage.getItem("empresaId");
      const empresaEmail =
        localStorage.getItem("empresaEmail") ||
        sessionStorage.getItem("empresaEmail");
      const empresaCnpj =
        localStorage.getItem("empresaCnpj") ||
        sessionStorage.getItem("empresaCnpj");

      if (empresaNome && empresaId) {
        // Usar dados do localStorage primeiro (mais rápido)
        const dadosEmpresa: Empresa = {
          id: parseInt(empresaId),
          nome: empresaNome,
          cnpj: empresaCnpj || "",
          email: empresaEmail || "",
          ativo: "S",
        };
        setEmpresa(dadosEmpresa);

        // Opcionalmente, buscar dados atualizados do backend
        try {
          const dadosAtualizados = await buscarEmpresaPorId(empresaId);
          if (dadosAtualizados) {
            setEmpresa(dadosAtualizados);
          } else {
            // Dados do backend não disponíveis, mantendo dados do localStorage
          }
        } catch (error) {
          // Erro ao buscar dados atualizados, usando dados do localStorage
        }
      } else if (empresaId) {
        // Fallback: buscar do backend se não tem dados no localStorage
        const dadosEmpresa = await buscarEmpresaPorId(empresaId);
        if (dadosEmpresa) {
          setEmpresa(dadosEmpresa);
        }
      } else {
        console.error("ID da empresa não encontrado no localStorage");
      }
    } catch (error) {
      console.error("Erro ao carregar dados da empresa:", error);
    }
  };

  const carregarEstatisticas = async () => {
    setLoading(true);
    try {
      const dados = await buscarEstatisticasEmpresa();
      setStats(dados);
    } catch (error: any) {
      // Tratamento específico para diferentes tipos de erro
      if (error?.response?.status === 500) {
        console.warn(
          "Servidor indisponível (500) - mantendo últimas estatísticas",
        );
      } else if (error?.response?.status === 503) {
        console.warn(
          "Serviço indisponível (503) - mantendo últimas estatísticas",
        );
      } else {
        console.error("Erro ao carregar estatísticas:", error);
      }
      // Definir valores padrão para poder exibir os cards
      setStats({
        minhasVagas: 0,
        vagasAtivas: 2,
        candidaturasPendentes: 0,
        candidatosAprovados: 3,
        vagasFinalizadas: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  const carregarCandidatosContratados = async () => {
    try {
      // Buscar candidatos contratados do banco de dados
      const { buscarCandidatosContratados } = await import('../../services/vaga');
      const resultado = await buscarCandidatosContratados();
      setCandidatosContratados(resultado.data.length);
    } catch (error) {
      console.error("Erro ao carregar candidatos contratados:", error);
      setCandidatosContratados(0);
    }
  };

  const carregarRankingCandidatos = async () => {
    try {
      const apiUrl = getApiBaseUrl();
      const empresaId =
        localStorage.getItem("empresaId") ||
        sessionStorage.getItem("empresaId");

      if (!empresaId) {
        console.error("ID da empresa não encontrado para ranking");
        return;
      }

      // Buscar todas as candidaturas usando serviços que filtram candidatos inativos
      let candidaturasPendentes: any[] = [];
      let candidatosProcessados: any[] = [];

      try {
        const resultadoPendentes = await buscarCandidaturasPendentes();
        candidaturasPendentes = resultadoPendentes.data;
      } catch (error) {
        console.error("Erro ranking pendentes:", error);
      }

      try {
        const resultadoProcessados = await buscarCandidatosProcessados();
        candidatosProcessados = resultadoProcessados.data;
      } catch (error) {
        console.error("Erro ranking processados:", error);
      }

      // Consolidar todas as candidaturas filtradas
      const todasCandidaturas = [
        ...candidaturasPendentes,
        ...candidatosProcessados,
      ];

      // Agrupar por candidato e contar - adaptado para dados dos serviços
      const candidatosMap = new Map();

      todasCandidaturas.forEach((candidatura: any) => {
        // Para candidaturas pendentes (CandidaturaPendente)
        let candidatoId =
          candidatura.id_candidato?.id || candidatura.candidatoId;
        let nome = candidatura.id_candidato?.nome || candidatura.nomeCandidato;
        let email =
          candidatura.id_candidato?.email || candidatura.emailCandidato;
        let dataAplicacao =
          candidatura.data_aplicacao || candidatura.dataCandidatura;

        // Para candidatos processados (CandidatoProcessado) - usar dados diretos
        if (!candidatoId && candidatura.candidatoNome) {
          // Gerar ID baseado no email para candidatos processados
          candidatoId = candidatura.candidatoEmail
            ? candidatura.candidatoEmail.replace(/[^a-zA-Z0-9]/g, "")
            : Math.random().toString();
          nome = candidatura.candidatoNome;
          email = candidatura.candidatoEmail;
          dataAplicacao = candidatura.dataAprovacao || candidatura.dataRejeicao;
        }

        // Fallbacks
        nome = nome || "Nome não informado";
        email = email || "Email não informado";
        dataAplicacao = dataAplicacao || new Date().toISOString();

        if (candidatosMap.has(candidatoId)) {
          const candidato = candidatosMap.get(candidatoId);
          candidato.totalCandidaturas++;
          // Manter a data mais recente
          if (new Date(dataAplicacao) > new Date(candidato.ultimaCandidatura)) {
            candidato.ultimaCandidatura = dataAplicacao;
          }
        } else {
          candidatosMap.set(candidatoId, {
            nome,
            email,
            totalCandidaturas: 1,
            ultimaCandidatura: dataAplicacao,
          });
        }
      });

      // Converter para array e ordenar por número de candidaturas
      const rankingArray = Array.from(candidatosMap.values())
        .sort((a, b) => b.totalCandidaturas - a.totalCandidaturas)
        .slice(0, 5); // Top 5

      setRankingCandidatos(rankingArray);
      setLoadingCharts(false);
    } catch (error) {
      console.error("Erro ao carregar ranking de candidatos:", error);
      setRankingCandidatos([]);
      setLoadingCharts(false);
    }
  };

  // Listener para atualizar quando candidatos forem contratados
  useEffect(() => {
    const handleCandidatoContratado = () => {
      carregarCandidatosContratados();
      // Recarregar estatísticas para atualizar o card de candidatos aprovados
      carregarEstatisticas();
    };

    window.addEventListener("candidatoContratado", handleCandidatoContratado);

    return () => {
      window.removeEventListener(
        "candidatoContratado",
        handleCandidatoContratado,
      );
    };
  }, []);

  const carregarRankingVagas = async () => {
    try {
      const apiUrl = getApiBaseUrl();
      const empresaId =
        localStorage.getItem("empresaId") ||
        sessionStorage.getItem("empresaId");

      if (!empresaId) {
        console.error("ID da empresa não encontrado para ranking vagas");
        return;
      }

      // Buscar todas as candidaturas usando os serviços existentes
      let candidaturasPendentes: any[] = [];
      let candidatosProcessados: any[] = [];

      try {
        const resultadoPendentes = await buscarCandidaturasPendentes();
        candidaturasPendentes = resultadoPendentes.data;
      } catch (error) {
        console.error("Erro candidaturas pendentes:", error);
      }

      try {
        const resultadoProcessados = await buscarCandidatosProcessados();
        candidatosProcessados = resultadoProcessados.data;
      } catch (error) {
        console.error("Erro candidatos processados:", error);
      }

      // Consolidar todas as candidaturas
      const todasCandidaturas = [
        ...candidaturasPendentes,
        ...candidatosProcessados,
      ];

      // Agrupar por vaga e contar candidaturas
      const vagasMap = new Map();

      todasCandidaturas.forEach((candidatura: any) => {
        // Extrair dados da vaga
        let vagaId = candidatura.id_vaga?.id || candidatura.vagaId;
        let tituloVaga =
          candidatura.id_vaga?.titulo ||
          candidatura.tituloVaga ||
          "Vaga não identificada";
        let dataPublicacao =
          candidatura.id_vaga?.data_publicacao ||
          candidatura.dataPublicacao ||
          new Date().toISOString();

        if (vagaId) {
          if (vagasMap.has(vagaId)) {
            const vaga = vagasMap.get(vagaId);
            vaga.totalCandidaturas++;
          } else {
            vagasMap.set(vagaId, {
              id: vagaId,
              titulo: tituloVaga,
              totalCandidaturas: 1,
              dataPublicacao: dataPublicacao,
            });
          }
        }
      });

      // Converter para array e ordenar por total de candidaturas (decrescente)
      const rankingArray = Array.from(vagasMap.values()).sort(
        (a, b) => b.totalCandidaturas - a.totalCandidaturas,
      );

      setRankingVagas(rankingArray);
    } catch (error) {
      console.error("Erro ao carregar ranking de vagas:", error);
      setRankingVagas([]);
    }
  };

  const formatarNumero = (num: number): string => {
    if (num === undefined || num === null || isNaN(num)) {
      return "0";
    }
    return num.toLocaleString("pt-BR");
  };

  const statsCards: StatCard[] = [
    {
      title: "Vagas Ativas",
      value: formatarNumero(stats.vagasAtivas),
      icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
      color: "#2C3E50", // Azul petróleo
      trend: "",
    },
    {
      title: "Candidatos Aprovados",
      value: formatarNumero(stats.candidatosAprovados),
      icon: <CheckCircleIcon sx={{ fontSize: 48 }} />,
      color: "#5B7FC7", // Azul metálico mais claro, elegante e discreto
      trend: "",
    },
    {
      title: "Vagas Finalizadas",
      value: formatarNumero(stats.vagasFinalizadas),
      icon: <HiredIcon sx={{ fontSize: 48 }} />,
      color: "#7F8C8D", // Cinza grafite (neutro, transmite fechamento)
      trend: "",
    },
    {
      title: "Candidatos Contratados",
      value: formatarNumero(candidatosContratados),
      icon: <HiredIcon sx={{ fontSize: 48 }} />,
      color: "#009688", // Teal elegante (mesma cor do botão Candidatar-se)
      trend: "",
    },
  ];

  if (!obterIdEmpresa()) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3, lg: 4 }, mt: 2 }}>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/login-empresa?logout=true")}
            >
              Entrar como empresa
            </Button>
          }
        >
          Este painel é exclusivo para empresas. A sessão atual não é de uma
          empresa (por exemplo, está logada como prefeitura). Entre com o
          CNPJ/CPF e a senha da empresa para ver suas vagas e candidatos.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        px: { xs: 1, sm: 2, md: 3, lg: 4 },
        position: "relative",
      }}
    >
      <Box
        sx={{
          mb: 2,
          mt: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            color: darkMode ? "#fff" : "#333",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          Olá, Bem-Vindo{" "}
          {(() => {
            const nomeEmpresa =
              empresa?.nome ||
              localStorage.getItem("empresaNome") ||
              sessionStorage.getItem("empresaNome");
            return nomeEmpresa || "Empresa";
          })()}
        </Typography>

        <Tooltip
          title={
            darkMode ? "Alternar para Modo Claro" : "Alternar para Modo Escuro"
          }
          enterDelay={1000}
          placement="left"
          arrow
        >
          <IconButton
            onClick={toggleDarkMode}
            sx={{
              backgroundColor: darkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.04)",
              "&:hover": {
                backgroundColor: darkMode
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // Mobile: 1 coluna
            sm: "repeat(2, 1fr)", // Small: 2 colunas
            md: "repeat(4, 1fr)", // Medium: 4 colunas alinhadas com linha 2
            lg: "repeat(4, 1fr)", // Large: 4 colunas alinhadas com linha 2
            xl: "repeat(4, 1fr)", // Extra Large: 4 colunas alinhadas com linha 2
          },
          gap: { xs: 1, sm: 1.5, md: 2 },
          mb: 3,
          perspective: "1000px",
          perspectiveOrigin: "center center",
        }}
      >
        {statsCards.map((stat, index) => (
          <Paper
            key={index}
            elevation={3}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              position: "relative",
              overflow: "hidden",
              borderRadius: 2,
              background:
                stat.title === "Candidatos Contratados"
                  ? "linear-gradient(135deg, #009688 0%, #26a69a 50%, #4db6ac 100%)" // Gradiente teal elegante
                  : stat.title === "Candidatos Aprovados"
                  ? "linear-gradient(135deg, #1976d2 0%, #2196f3 50%, #42a5f5 100%)" // Gradiente azul puro
                  : `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 40%, ${stat.color}bb 100%)`,
              color: "white",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "130px",
              cursor: "default",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Skeleton
                  variant="circular"
                  width={50}
                  height={50}
                  sx={{ mb: 2, bgcolor: "rgba(255,255,255,0.2)" }}
                />
                <Skeleton
                  variant="text"
                  width="80%"
                  sx={{ mb: 1, bgcolor: "rgba(255,255,255,0.2)" }}
                />
                <Skeleton
                  variant="text"
                  width="60%"
                  height={40}
                  sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    mb: 1.5,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box
                  sx={{
                    fontSize: "0.9rem",
                    mb: 1,
                    textAlign: "center",
                    opacity: 0.9,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body2" component="span">
                    {stat.title}
                  </Typography>
                  {stat.hasNew && (
                    <>
                      {stat.newCount && stat.newCount > 0 && (
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            backgroundColor: "#ff4444",
                            color: "white",
                            borderRadius: "50%",
                            minWidth: 18,
                            height: 18,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.7rem",
                            fontWeight: "bold",
                            ml: 0.5,
                          }}
                        >
                          {stat.newCount > 99 ? "99+" : stat.newCount}
                        </Typography>
                      )}
                      <CircleNotificationsIcon
                        sx={{ fontSize: 16, color: "#ff4444", ml: 0.5 }}
                      />
                    </>
                  )}
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: "2.5rem",
                  }}
                >
                  {stat.value}
                </Typography>
              </>
            )}
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          mt: 3,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr", // Mobile: 1 coluna
            sm: "1fr", // Small: 1 coluna
            md: "repeat(2, 1fr)", // Medium: 2 colunas
            lg: "repeat(2, 1fr)", // Large: 2 colunas
            xl: "repeat(2, 1fr)", // Extra Large: 2 colunas
          },
          gap: { xs: 1, sm: 1.5, md: 2, lg: 2.5, xl: 3 },
          pb: 2,
        }}
      >
              </Box>
                      </Box>
                    );
}

export default DashboardEmpresa;
